import { NgClass } from '@angular/common';
import { AfterViewInit, Component, computed, ContentChild, ElementRef, inject, input, signal } from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BadgeComponent } from '../badge/badge.component';
import { IconComponent } from '../icons/icon/icon.component';

const RADIO_GROUP_STYLE_CLASS = 'ui-radio-button-parent-group';

export type RadioButtonTooltip =
  | string
  | {
      text?: string;
      callback?: () => void;
    };

@Component({
  selector: 'ui-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrl: './radio-button.component.scss',
  standalone: true,
  imports: [MatRadioModule, NgClass, IconComponent, MatTooltipModule],
})
export class RadioButtonComponent implements AfterViewInit {
  readonly label = input.required<string>();
  readonly value = input.required<unknown>();
  readonly description = input<string>();
  readonly disabled = input<boolean>(false);
  readonly withBorder = input<boolean>(true);
  readonly disableGroupStyle = input<boolean>(false);
  readonly size = input<'regular' | 'large'>('regular');
  readonly tooltip = input<RadioButtonTooltip>();

  @ContentChild(BadgeComponent) projectedBadge?: BadgeComponent;

  readonly sizeClass = computed(() => `ui-radio-button-${this.size()}`);
  readonly borderClass = computed(() => (this.withBorder() ? 'ui-radio-button-with-border' : ''));
  readonly disabledClass = signal<string>('');

  readonly hasBadge = computed(() => this.projectedBadge !== undefined);
  readonly tooltipText = computed(() => {
    const tooltip = this.tooltip();
    if (typeof tooltip === 'string') {
      return tooltip;
    } else {
      return tooltip?.text || '';
    }
  });

  readonly elementRef = inject(ElementRef);

  ngAfterViewInit(): void {
    this.checkDisabled();
    this.styleFormGroup();
  }

  handleTooltipIconClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const tooltip = this.tooltip();
    if (typeof tooltip !== 'string' && tooltip?.callback) tooltip.callback();
  }

  private checkDisabled(): void {
    const disabledRadio = this.elementRef.nativeElement.querySelector('.mdc-radio--disabled');
    if (disabledRadio) {
      this.disabledClass.set('ui-radio-button-disabled');
    }
  }

  private styleFormGroup(): void {
    if (this.disableGroupStyle()) return;

    const formGroup = this.elementRef.nativeElement.closest('mat-radio-group');

    if (formGroup && !formGroup.classList.contains(RADIO_GROUP_STYLE_CLASS)) {
      formGroup.classList.add(RADIO_GROUP_STYLE_CLASS);
      formGroup.style.display = 'grid';
      formGroup.style.rowGap = '8px';
    }
  }
}
